export const HANSCRIBE_VERSION = '0.1.0';

export const HANSCRIBE_APP_IDS = [
    'encounter.board',
    'encounter.workspace',
    'interview.input',
    'patient.summary',
    'checklist.panel',
    'soap.editor',
    'final.chart',
    'audit.log',
];

export const HANSCRIBE_ROUTES = {
    precheck: '/api/hanscribe/precheck/<path:path>',
    aiRuns: '/api/hanscribe/ai/runs',
};

export const HANSCRIBE_AI_TASK_TYPES = [
    'summary.generate',
    'soap.draft.generate',
] as const;

export const HANSCRIBE_ROLE_LABELS: Record<string, string> = {
    doctor: '한의사',
    operator: '원무/운영',
    admin: '관리자',
};

export const HANSCRIBE_ACTION_ROLES: Record<string, string[]> = {
    board_view: ['doctor', 'operator', 'admin'],
    board_create_visit: ['operator', 'admin'],
    board_delete_visit: ['operator', 'admin'],
    board_start_encounter: ['doctor'],
    workspace_view: ['doctor', 'admin'],
    clinical_ai_generate: ['doctor'],
    clinical_soap_edit: ['doctor'],
    clinical_final_approve: ['doctor'],
    demo_fixture_seed: ['admin'],
    demo_fixture_reset: ['doctor', 'admin'],
    demo_fixture_create_clean: ['admin'],
    qa_fixture_seed: ['admin'],
    account_manage: ['admin'],
};

export type HanscribeBadgeTone = 'neutral' | 'primary' | 'info' | 'ai' | 'success' | 'warning' | 'pending' | 'danger';

const ENCOUNTER_STATUS_TONES: Record<string, HanscribeBadgeTone> = {
    scheduled: 'info',
    waiting: 'warning',
    checked_in: 'warning',
    in_progress: 'primary',
    checkout_pending: 'pending',
    done: 'success',
    cancelled: 'neutral',
    no_show: 'neutral',
    left_without_care: 'warning',
};

const PRECHECK_STATUS_TONES: Record<string, HanscribeBadgeTone> = {
    none: 'neutral',
    pending: 'neutral',
    draft: 'warning',
    submitted: 'success',
};

const COMMON_STATUS_TONES: Record<string, HanscribeBadgeTone> = {
    ready: 'success',
    saved: 'success',
    synced: 'success',
    started: 'primary',
    completed: 'success',
    passed: 'success',
    approved: 'success',
    already_approved: 'success',
    submitted: 'success',
    reviewed: 'success',
    accepted: 'success',
    referenced: 'success',
    applied: 'success',
    action_completed: 'success',
    final_saved: 'success',
    ready_for_approval: 'primary',
    ready_for_final_save: 'primary',
    candidate_ready: 'ai',
    new_candidate_ready: 'warning',
    candidate_preparing: 'ai',
    generating: 'ai',
    draft_generated: 'ai',
    manual_draft: 'info',
    doctor_added: 'info',
    recommended: 'info',
    empty: 'neutral',
    no_candidate: 'neutral',
    skeleton: 'neutral',
    no_draft: 'neutral',
    previous_version: 'neutral',
    not_found: 'neutral',
    unchecked: 'warning',
    waiting: 'warning',
    checked_in: 'warning',
    pending_doctor_review: 'warning',
    pending_apply: 'warning',
    ai_unreviewed: 'warning',
    dirty: 'warning',
    draft: 'warning',
    inputting: 'warning',
    saving: 'warning',
    needs_temp_save: 'warning',
    deferred: 'pending',
    accepted_edited: 'pending',
    soap_suggested: 'pending',
    doctor_edited: 'pending',
    needs_regeneration: 'pending',
    validation_failed: 'danger',
    forbidden: 'danger',
    excluded: 'danger',
    config_missing: 'danger',
    policy_blocked: 'danger',
    provider_failed: 'danger',
    snapshot_failed: 'danger',
    error: 'danger',
};

export function hanscribeRole(role: any): string {
    return String(role || '').trim();
}

export function hanscribeRoleLabel(role: any): string {
    const normalized = hanscribeRole(role);
    return HANSCRIBE_ROLE_LABELS[normalized] || 'Hanscribe 내부 계정';
}

export function hanscribeCan(role: any, action: string): boolean {
    const roles = HANSCRIBE_ACTION_ROLES[action] || [];
    if (!roles.length) return false;
    return roles.indexOf(hanscribeRole(role)) >= 0;
}

export function hanscribeBadgeClass(tone: HanscribeBadgeTone = 'neutral', base: string = 'hs-badge'): string {
    return `${base} ${base}--${tone}`;
}

export function hanscribeStatusTone(status: any, domain: string = 'common'): HanscribeBadgeTone {
    const normalized = String(status || '').trim();
    if (!normalized) return 'neutral';
    if (domain === 'encounter' && ENCOUNTER_STATUS_TONES[normalized]) return ENCOUNTER_STATUS_TONES[normalized];
    if (domain === 'precheck' && PRECHECK_STATUS_TONES[normalized]) return PRECHECK_STATUS_TONES[normalized];
    return COMMON_STATUS_TONES[normalized] || 'neutral';
}

export function hanscribeStatusBadgeClass(status: any, domain: string = 'common', base: string = 'hs-badge'): string {
    return hanscribeBadgeClass(hanscribeStatusTone(status, domain), base);
}

export function hanscribePrecheckBadgeClass(submittedOrStatus: any, base: string = 'hs-badge'): string {
    const status = submittedOrStatus === true ? 'submitted' : String(submittedOrStatus || 'pending').trim();
    return hanscribeStatusBadgeClass(status, 'precheck', base);
}
